import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { FormikValues } from "formik";
import { BillComValidation } from "formValidations/BillComValidation";
import { useToast } from "hooks/useToast";
import { ReactNode, useState } from "react";
import { useConfirmAccountsMutation } from "store/apis/bankConnections";
import { BackendError } from "types/utils/error";

const Step = ({
  number,
  children,
  isLast = false,
}: {
  number: number;
  children: ReactNode;
  isLast?: boolean;
}) => (
  <div className="t-flex t-gap-3 ">
    <div className="t-flex t-flex-col t-items-center">
      <div className="t-text-white t-bg-purple-40 t-rounded-full t-text-caption t-text-center t-h-4 t-w-4 t-py-[1px] t-px-1">
        {number}
      </div>
      {!isLast && (
        <div className="t-w-0.5 t-h-full t-bg-neutral-0 t-my-0.5 t-min-h-4" />
      )}
    </div>
    <div className="t-text-text-60 t-text-body-sm [&>b]:t-text-subtext t-self-start -t-mt-1">
      {children}
      <div className="t-invisible">
        This is a placeholder for additional content
      </div>
    </div>
  </div>
);

export const ConnectBillDotCom = ({
  entityId,
  connectionId,
  onSuccess,
}: {
  entityId: string;
  connectionId: string;
  onSuccess?: () => void;
}) => {
  const { alertToast, info: infoToast } = useToast();

  const [confirmAccounts, { isLoading }] = useConfirmAccountsMutation();
  const [step, setStep] = useState<"instructions" | "credentials">(
    "instructions"
  );
  const handleNext = () => setStep("credentials");

  const handleConnectBillCom = async (values: FormikValues) => {
    const code = [
      values.syncToken,
      values.tokenPassword,
      values.organizationId,
      values.developerKey,
    ].join("|");

    try {
      await confirmAccounts({
        entityId,
        payload: {
          mapping_type: "DIRECT",
          code,
          connection_id: connectionId,
          account_mappers: [],
        },
      }).unwrap();

      infoToast({
        title: "Bill.com connected",
        message: "Your Bill.com account has been successfully connected.",
      });

      onSuccess?.();
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  return (
    <Modal.Content asChild size="large">
      <FormikForm
        onSubmit={handleConnectBillCom}
        initialValues={{
          syncToken: "",
          tokenPassword: "",
          organizationId: "",
          developerKey: "",
        }}
        validationSchema={BillComValidation}
      >
        {({ isValid, dirty }) => (
          <>
            <Modal.Header>
              <Modal.Title>Connect Bill.com</Modal.Title>
              <Modal.Close />
            </Modal.Header>
            <Modal.Body className="t-flex t-flex-col t-gap-6">
              <div className="t-flex t-flex-col t-gap-2">
                <div className="t-text-text-30 t-text-body-sm">
                  Follow these simple steps to connect your Bill.com account
                </div>
              </div>
              <div className="t-flex t-flex-col t-gap-2">
                <Step number={1}>
                  Log in to your 
                  <a
                    href="https://login.us.bill.com/neo/login"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="t-text-text-60 t-underline"
                  >
                    Bill.com account.
                  </a>{" "}
                </Step>
                <Step number={2}>
                  In the left menu, go to{" "}
                  <b>{"Settings > Sync & Integrations > Tokens."}</b>
                </Step>
                <Step number={3}>
                  On the top right, click on “<b>New</b>” to create a new sync
                  token & password.
                </Step>
                <Step number={4}>
                  Now go to{" "}
                  <b>{"Sync & Integrations > Manage Developer Keys."}</b>
                </Step>
                <Step number={5}>
                  On the top right, click on “<b>Generate developer key</b>” to
                  create a new developer key.
                </Step>
                <Step number={6}>
                  Just below the list of developer keys, you can also find the “
                  <b>Organization ID</b>”.
                </Step>
                <Step number={7} isLast={true}>
                  Return here and enter the <b>generated token & key details</b>{" "}
                  below to complete the connection.
                </Step>
                <div className="t-ml-7">
                  {step === "instructions" && (
                    <Button
                      customType="primary_outlined"
                      size="small"
                      onClick={handleNext}
                    >
                      Enter Details
                    </Button>
                  )}
                  {step === "credentials" && (
                    <div className="t-grid t-grid-cols-2 t-gap-4">
                      <TextInput
                        showErrorOnceTouched
                        label="Sync Token"
                        required
                        name="syncToken"
                      />
                      <TextInput
                        showErrorOnceTouched
                        name="tokenPassword"
                        label="Token Password"
                        required
                      />
                      <TextInput
                        showErrorOnceTouched
                        label="Organization ID"
                        required
                        name="organizationId"
                      />
                      <TextInput
                        showErrorOnceTouched
                        name="developerKey"
                        label="Developer Key"
                        required
                      />
                    </div>
                  )}
                </div>
              </div>
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <Modal.RawClose asChild>
                <Button disabled={isLoading}>Cancel</Button>
              </Modal.RawClose>
              <Button
                customType="primary"
                isLoading={isLoading}
                disabled={!isValid || isLoading || !dirty}
                type="submit"
              >
                Connect
              </Button>
            </Modal.FooterButtonGroup>
          </>
        )}
      </FormikForm>
    </Modal.Content>
  );
};
